import type { Locale } from "./i18n";

export const handoffTranslationKeys = [
  "continueOnIOSDevice",
  "downloadForMac",
  "desktopHandoffBody",
  "desktopHandoffQrAlt",
  "desktopHandoffLoading",
  "desktopHandoffUnavailable",
] as const;

export type HandoffTranslationKey = (typeof handoffTranslationKeys)[number];

type HandoffStrings = Record<HandoffTranslationKey, string>;

/** Complete copy for the desktop-to-iPhone/iPad handoff in every web locale. */
export const handoffTranslations: Record<Locale, HandoffStrings> = {
  en: {
    continueOnIOSDevice: "Continue on iPhone or iPad",
    downloadForMac: "Download for Mac",
    desktopHandoffBody: "Scan with the Camera app on your iPhone or iPad. Backtest will open with this setup preloaded.",
    desktopHandoffQrAlt: "QR code to continue this setup in Backtest on iPhone or iPad",
    desktopHandoffLoading: "Preparing QR code…",
    desktopHandoffUnavailable: "The QR code is unavailable. Use the App Store link instead.",
  },
  vi: {
    continueOnIOSDevice: "Tiếp tục trên iPhone hoặc iPad",
    downloadForMac: "Tải cho Mac",
    desktopHandoffBody: "Quét bằng ứng dụng Camera trên iPhone hoặc iPad. Backtest sẽ mở sẵn thiết lập này.",
    desktopHandoffQrAlt: "Mã QR để tiếp tục thiết lập này trong Backtest trên iPhone hoặc iPad",
    desktopHandoffLoading: "Đang tạo mã QR…",
    desktopHandoffUnavailable: "Không thể tạo mã QR. Hãy dùng liên kết App Store.",
  },
  hi: {
    continueOnIOSDevice: "iPhone या iPad पर जारी रखें",
    downloadForMac: "Mac के लिए डाउनलोड करें",
    desktopHandoffBody: "अपने iPhone या iPad के Camera ऐप से स्कैन करें। Backtest इस सेटअप के साथ खुलेगा।",
    desktopHandoffQrAlt: "iPhone या iPad पर Backtest में यह सेटअप जारी रखने का QR कोड",
    desktopHandoffLoading: "QR कोड तैयार हो रहा है…",
    desktopHandoffUnavailable: "QR कोड उपलब्ध नहीं है। इसके बजाय App Store लिंक का उपयोग करें।",
  },
  ur: {
    continueOnIOSDevice: "iPhone یا iPad پر جاری رکھیں",
    downloadForMac: "Mac کے لیے ڈاؤن لوڈ کریں",
    desktopHandoffBody: "اپنے iPhone یا iPad کی Camera ایپ سے اسکین کریں۔ Backtest اسی سیٹ اپ کے ساتھ کھلے گا۔",
    desktopHandoffQrAlt: "iPhone یا iPad پر Backtest میں یہ سیٹ اپ جاری رکھنے کا QR کوڈ",
    desktopHandoffLoading: "QR کوڈ تیار ہو رہا ہے…",
    desktopHandoffUnavailable: "QR کوڈ دستیاب نہیں۔ اس کے بجائے App Store لنک استعمال کریں۔",
  },
  th: {
    continueOnIOSDevice: "ทำต่อบน iPhone หรือ iPad",
    downloadForMac: "ดาวน์โหลดสำหรับ Mac",
    desktopHandoffBody: "สแกนด้วยแอปกล้องบน iPhone หรือ iPad แล้ว Backtest จะเปิดพร้อมการตั้งค่านี้",
    desktopHandoffQrAlt: "คิวอาร์โค้ดสำหรับทำการตั้งค่านี้ต่อใน Backtest บน iPhone หรือ iPad",
    desktopHandoffLoading: "กำลังเตรียมคิวอาร์โค้ด…",
    desktopHandoffUnavailable: "ไม่สามารถสร้างคิวอาร์โค้ดได้ โปรดใช้ลิงก์ App Store แทน",
  },
  bn: {
    continueOnIOSDevice: "iPhone বা iPad-এ চালিয়ে যান",
    downloadForMac: "Mac-এর জন্য ডাউনলোড করুন",
    desktopHandoffBody: "আপনার iPhone বা iPad-এর Camera অ্যাপ দিয়ে স্ক্যান করুন। এই সেটআপসহ Backtest খুলবে।",
    desktopHandoffQrAlt: "iPhone বা iPad-এ Backtest-এ এই সেটআপ চালিয়ে যাওয়ার QR কোড",
    desktopHandoffLoading: "QR কোড তৈরি হচ্ছে…",
    desktopHandoffUnavailable: "QR কোড পাওয়া যাচ্ছে না। পরিবর্তে App Store লিংক ব্যবহার করুন।",
  },
  id: {
    continueOnIOSDevice: "Lanjutkan di iPhone atau iPad",
    downloadForMac: "Unduh untuk Mac",
    desktopHandoffBody: "Pindai dengan aplikasi Kamera di iPhone atau iPad. Backtest akan terbuka dengan setup ini.",
    desktopHandoffQrAlt: "Kode QR untuk melanjutkan setup ini di Backtest pada iPhone atau iPad",
    desktopHandoffLoading: "Menyiapkan kode QR…",
    desktopHandoffUnavailable: "Kode QR tidak tersedia. Gunakan tautan App Store.",
  },
  fil: {
    continueOnIOSDevice: "Magpatuloy sa iPhone o iPad",
    downloadForMac: "I-download para sa Mac",
    desktopHandoffBody: "I-scan gamit ang Camera app sa iPhone o iPad. Magbubukas ang Backtest na handa na ang setup na ito.",
    desktopHandoffQrAlt: "QR code para ipagpatuloy ang setup na ito sa Backtest sa iPhone o iPad",
    desktopHandoffLoading: "Inihahanda ang QR code…",
    desktopHandoffUnavailable: "Hindi available ang QR code. Gamitin na lang ang App Store link.",
  },
  "es-419": {
    continueOnIOSDevice: "Continuar en iPhone o iPad",
    downloadForMac: "Descargar para Mac",
    desktopHandoffBody: "Escanea con la app Cámara de tu iPhone o iPad. Backtest se abrirá con esta configuración lista.",
    desktopHandoffQrAlt: "Código QR para continuar esta configuración en Backtest en iPhone o iPad",
    desktopHandoffLoading: "Preparando el código QR…",
    desktopHandoffUnavailable: "El código QR no está disponible. Usa el enlace de App Store.",
  },
  "pt-BR": {
    continueOnIOSDevice: "Continuar no iPhone ou iPad",
    downloadForMac: "Baixar para Mac",
    desktopHandoffBody: "Escaneie com o app Câmera do iPhone ou iPad. O Backtest abrirá com esta configuração pronta.",
    desktopHandoffQrAlt: "Código QR para continuar esta configuração no Backtest no iPhone ou iPad",
    desktopHandoffLoading: "Preparando o código QR…",
    desktopHandoffUnavailable: "O código QR não está disponível. Use o link da App Store.",
  },
  ja: {
    continueOnIOSDevice: "iPhoneまたはiPadで続ける",
    downloadForMac: "Mac版をダウンロード",
    desktopHandoffBody: "iPhoneまたはiPadのカメラで読み取ってください。この設定を引き継いでBacktestが開きます。",
    desktopHandoffQrAlt: "iPhoneまたはiPadのBacktestでこの設定を続けるためのQRコード",
    desktopHandoffLoading: "QRコードを準備しています…",
    desktopHandoffUnavailable: "QRコードを利用できません。App Storeのリンクをご利用ください。",
  },
  ko: {
    continueOnIOSDevice: "iPhone 또는 iPad에서 계속",
    downloadForMac: "Mac용 다운로드",
    desktopHandoffBody: "iPhone 또는 iPad의 카메라 앱으로 스캔하세요. 이 설정이 준비된 상태로 Backtest가 열립니다.",
    desktopHandoffQrAlt: "iPhone 또는 iPad의 Backtest에서 이 설정을 계속하기 위한 QR 코드",
    desktopHandoffLoading: "QR 코드를 준비하는 중…",
    desktopHandoffUnavailable: "QR 코드를 사용할 수 없습니다. App Store 링크를 이용하세요.",
  },
  ru: {
    continueOnIOSDevice: "Продолжить на iPhone или iPad",
    downloadForMac: "Скачать для Mac",
    desktopHandoffBody: "Отсканируйте код камерой iPhone или iPad. Backtest откроется с готовыми настройками.",
    desktopHandoffQrAlt: "QR-код для продолжения этой настройки в Backtest на iPhone или iPad",
    desktopHandoffLoading: "Подготовка QR-кода…",
    desktopHandoffUnavailable: "QR-код недоступен. Используйте ссылку App Store.",
  },
  tr: {
    continueOnIOSDevice: "iPhone veya iPad’de devam et",
    downloadForMac: "Mac için indirin",
    desktopHandoffBody: "iPhone veya iPad’inizde Kamera uygulamasıyla tarayın. Backtest bu kurulum hazır olarak açılır.",
    desktopHandoffQrAlt: "Bu kuruluma iPhone veya iPad’de Backtest ile devam etmek için QR kodu",
    desktopHandoffLoading: "QR kodu hazırlanıyor…",
    desktopHandoffUnavailable: "QR kodu kullanılamıyor. Bunun yerine App Store bağlantısını kullanın.",
  },
  uk: {
    continueOnIOSDevice: "Продовжити на iPhone або iPad",
    downloadForMac: "Завантажити для Mac",
    desktopHandoffBody: "Відскануйте код камерою iPhone або iPad. Backtest відкриється з готовими налаштуваннями.",
    desktopHandoffQrAlt: "QR-код для продовження цього налаштування в Backtest на iPhone або iPad",
    desktopHandoffLoading: "Підготовка QR-коду…",
    desktopHandoffUnavailable: "QR-код недоступний. Скористайтеся посиланням App Store.",
  },
};
